import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";

// DTO interfaces (from backend)
export interface AccountGroupDto {
  id: number;
  name: string;
}

export interface AccountDto {
  id: number;
  uuid: string;
  name: string;
  email: string;
  phone: string;
  accountGroup: AccountGroupDto | null;
  invitesEnabled: boolean;
  signedCollectionAgreement: boolean;
}

export enum UserAccountRole {
  SUPER_ADMIN = "super-admin",
  ADMIN = "admin",
  USER = "user",
}

export interface UserMeDto {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  account: AccountDto;
  role: UserAccountRole;
}

// Client-side User type
export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  account: AccountDto;
  role: UserAccountRole;
}

// User account entry (for account switcher)
export interface UserAccountEntry {
  id: number;
  uuid: string;
  name: string;
  email: string;
  phone: string;
  role: UserAccountRole;
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  accounts: UserAccountEntry[];
  loading: boolean;
  loginSent: boolean;
  pendingEmail: string | null;
  sendCode: (email: string) => Promise<void>;
  validateCode: (email: string, code: string) => Promise<void>;
  validateAuthCode: (code: string) => Promise<void>;
  switchAccount: (accountId: number) => Promise<void>;
  setLoginSent: (sent: boolean) => void;
  setPendingEmail: (email: string | null) => void;
  logout: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * The stored token to start with. Arriving on /login-with-code (admin's
 * "Log in as User") always swaps accounts, so the previous session is
 * dropped up front: validating it first would race the code login, and if
 * it had expired the failed /users/me call logged out and navigated to
 * /login before the code was ever used.
 */
const initialToken = (): string | null => {
  if (window.location.pathname.startsWith("/login-with-code/")) {
    localStorage.removeItem("token");
    return null;
  }
  return localStorage.getItem("token");
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(initialToken);
  const [user, setUser] = useState<User | null>(null);
  const [accounts, setAccounts] = useState<UserAccountEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loginSent, setLoginSent] = useState<boolean>(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // A response for a token that's since been replaced (logout, code
    // login, account switch) must not overwrite the new session's user —
    // or log it out.
    let stale = false;
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // Fetch user and accounts in parallel
        const [userResponse, accountsResponse] = await Promise.all([
          axios.get<UserMeDto>(`${config.API_BASE_URL}/users/me`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get<UserAccountEntry[]>(`${config.API_BASE_URL}/users/me/accounts`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (stale) return;
        const data = userResponse.data;
        // Map DTO to client User
        setUser({
          id: data.id,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          account: data.account,
          role: data.role,
        });
        setAccounts(accountsResponse.data);
      } catch (error) {
        if (stale) return;
        console.error("Failed to fetch user info:", error);
        logout();
      } finally {
        if (!stale) setLoading(false);
      }
    };

    fetchUser();
    return () => {
      stale = true;
    };
  }, [token]);

  const sendCode = async (email: string) => {
    try {
      await axios.post(`${config.API_BASE_URL}/auth/login`, {
        email,
      });
    } catch (error) {
      console.error("Send code failed:", error);
      throw error;
    }
  };

  const validateCode = async (email: string, code: string) => {
    try {
      const response = await axios.post(`${config.API_BASE_URL}/auth/validate`, {
        email,
        code,
      });
      const authToken = response.data.token;
      localStorage.setItem("token", authToken);
      setToken(authToken);
      setLoginSent(false);
      setPendingEmail(null);
      navigate("/dashboard");
    } catch (error) {
      console.error("Code validation failed:", error);
      throw error;
    }
  };

  const validateAuthCode = async (code: string) => {
    try {
      const response = await axios.post(`${config.API_BASE_URL}/auth/code`, {
        code,
      });
      const authToken = response.data.token;
      localStorage.setItem("token", authToken);
      setToken(authToken);
      setLoginSent(false);
      setPendingEmail(null);
      navigate("/dashboard");
    } catch (error) {
      console.error("Auth code validation failed:", error);
      throw error;
    }
  };

  const switchAccount = async (accountId: number) => {
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/auth/switch-account`,
        { accountId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newToken = response.data.token;
      localStorage.setItem("token", newToken);
      setToken(newToken);
      // Navigate to dashboard and reload to refresh all data
      navigate("/dashboard");
      window.location.reload();
    } catch (error) {
      console.error("Failed to switch account:", error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setAccounts([]);
    setLoginSent(false);
    setPendingEmail(null);
    navigate("/login");
  };

  return (
    <AuthContext.Provider value={{
      token,
      user,
      accounts,
      loading,
      loginSent,
      pendingEmail,
      sendCode,
      validateCode,
      validateAuthCode,
      switchAccount,
      setLoginSent,
      setPendingEmail,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
